import React, { useEffect, useState } from "react";
import { getProtectedData } from "../services/api";
import { getCurrentPeriod } from "../helper/utils";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts';
import { Container, Row, Col, Table } from 'react-bootstrap';
import "./bess_strategy/bess_strategy.css";

const BESS_Strategy = () => {
  const [dprData, setDprData] = useState({});
  const [selectedStrategy, setSelectedStrategy] = useState("CR Priority");
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [recommendations, setRecommendations] = useState([]);

  const currentPeriod = getCurrentPeriod(); // Get the current period

  // Fetch data on page load
  useEffect(() => {
    getProtectedData("/getDpr", {}, processDprData);
  }, []);

  // Set up auto refresh every minute
  useEffect(() => {
    const interval = setInterval(() => setRefresh((r) => !r), 60 * 1000); // Refresh every minute
    return () => clearInterval(interval); // Cleanup interval on unmount
  }, []);

  // Force a refresh after 4 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      setRefresh((r) => !r); // Force refresh after 4 seconds
    }, 4000); // 4 seconds

    return () => clearTimeout(timeout); // Cleanup timeout on unmount or refresh
  }, []);

  useEffect(() => {
    if (!loading) {
      calculateStrategy(selectedStrategy); // Recalculate when refresh or strategy changes
    }
  }, [refresh, selectedStrategy]);

  const processDprData = (data) => {
    setDprData(data);
    setLoading(false); // Data loaded
  };

  const calculateStrategy = (strategy) => {
    const chartData = [];
    let hybridData = [];
    let contingencyData = [];
    let regulationData = [];
    let recommendationData = [];

    for (let period = 1; period <= 48; period++) {
      if (dprData[period]) {
        const contingencyPrice = dprData[period].Contingency;
        const regulationPrice = dprData[period].Regulation;

        let priceContingency = contingencyPrice;
        let priceRegulation = regulationPrice;

        switch (strategy) {
          case "CR Priority": // Contingency Priority
            contingencyData.push({ period, price: priceContingency, type: "Contingency" });
            break;
          case "FR Priority": // Regulation Priority
            regulationData.push({ period, price: priceRegulation, type: "Regulation" });
            break;
          case "Hybrid": // Hybrid: Show the higher of Contingency and Regulation
            const higherPrice = Math.max(priceContingency, priceRegulation);
            hybridData.push({ period, price: higherPrice, type: priceContingency > priceRegulation ? "Contingency" : "Regulation" });
            break;
          default:
            break;
        }
      }
    }

    // For CR Priority, select top 4 regulation price periods after the current period
    if (strategy === "CR Priority") {
      const futurePeriodsRegulation = Object.keys(dprData)
        .filter(period => parseInt(period) > currentPeriod)
        .map(period => ({
          period: parseInt(period),
          price: dprData[period].Regulation,
          type: "Regulation"
        }))
        .sort((a, b) => b.price - a.price) // Sort descending by price
        .slice(0, 4); // Get top 4

      setRecommendations(futurePeriodsRegulation);
    }

    // For FR Priority, select top 4 contingency price periods after the current period
    if (strategy === "FR Priority") {
      const futurePeriodsContingency = Object.keys(dprData)
        .filter(period => parseInt(period) > currentPeriod)
        .map(period => ({
          period: parseInt(period),
          price: dprData[period].Contingency,
          type: "Contingency"
        }))
        .sort((a, b) => b.price - a.price) // Sort descending by price
        .slice(0, 4); // Get top 4

      setRecommendations(futurePeriodsContingency);
    }

    // For Hybrid, select the top 8 periods after the current period where we choose the higher price
    if (strategy === "Hybrid") {
      const futurePeriodsHybrid = Object.keys(dprData)
        .filter(period => parseInt(period) > currentPeriod)
        .map(period => {
          const contingencyPrice = dprData[period].Contingency;
          const regulationPrice = dprData[period].Regulation;
          const higherPrice = Math.max(contingencyPrice, regulationPrice);
          const type = contingencyPrice > regulationPrice ? "Contingency" : "Regulation";
          return { period: parseInt(period), price: higherPrice, type };
        })
        .slice(0, 8); // Get next 8 periods

      setRecommendations(futurePeriodsHybrid);
    }

    if (strategy === "Hybrid") {
      setChartData({ hybridData });
    } else if (strategy === "CR Priority") {
      setChartData({ contingencyData });
    } else if (strategy === "FR Priority") {
      setChartData({ regulationData });
    }
  };

  const handleStrategyChange = (e) => {
    setSelectedStrategy(e.target.value);
    setRefresh((r) => !r); // Trigger a refresh when strategy changes
  };

  // Wait for data to load before rendering the graph
  if (loading) {
    return <p>Loading data...</p>;
  }

  // Sort recommendations by Period
  const sortedRecommendations = recommendations.sort((a, b) => a.period - b.period);

  return (
    <Container fluid className="p-0 flex-grow-0">
      {/* Top: Strategy Selector */}
      <Row className="mb-4">
        <Col>
          <h1>BESS Strategy</h1>
          <label htmlFor="strategy-selector">Select Strategy: </label>
          <select
            id="strategy-selector"
            value={selectedStrategy}
            onChange={handleStrategyChange}
            className="form-select"
          >
            <option value="CR Priority">CR Priority</option>
            <option value="FR Priority">FR Priority</option>
            <option value="Hybrid">Hybrid</option>
          </select>
        </Col>
      </Row>

      {/* Body: Graph of Period vs Price */}
      <Row style={{ marginBottom: "10px" }}>
        <Col>
          <h2>Period vs Price</h2>
          <LineChart
            width={window.innerWidth * 0.95}  // Use the width of the screen
            height={window.innerHeight * 0.45} // Use a portion of the screen height
            data={chartData.hybridData || chartData.contingencyData || chartData.regulationData}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip />
            <Legend />
            {selectedStrategy === "Hybrid" && (
              <Line
                type="monotone"
                dataKey="price"
                stroke="#8884d8" // A single line for hybrid, showing the higher price
                name="Hybrid Strategy"
                dot={false}
              />
            )}
            {selectedStrategy === "CR Priority" && (
              <Line
                type="monotone"
                dataKey="price"
                stroke="#8884d8"
                name="Contingency"
                dot={false}
              />
            )}
            {selectedStrategy === "FR Priority" && (
              <Line
                type="monotone"
                dataKey="price"
                stroke="#82ca9d"
                name="Regulation"
                dot={false}
              />
            )}

            {/* Add Current Period Vertical Line */}
            <ReferenceLine
              x={currentPeriod} // Current period to place the vertical line
              stroke="#ff7300"  // Line color
              strokeWidth={2} // Line width
              strokeDasharray="3 3" // Dotted line after current period
            />
          </LineChart>
        </Col>
      </Row>

      {/* Recommendations Section */}
      {recommendations.length > 0 && (
        <Row className="mb-4" style={{ marginTop: "5px" }}>
          <Col>
            <h3>Recommendation</h3>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Type</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {sortedRecommendations.map((item, index) => (
                  <tr key={index}>
                    <td>{item.period}</td>
                    <td>{item.type}</td>
                    <td>${item.price.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default BESS_Strategy;
